import { useMemo } from 'react'
import { getBanner } from 'api/banner'
import { getTestimonialPostBySlug, getTestimonialPostsSlug } from 'api/posts'
import blocks from 'blocks/posts'
import { NavAndFooterLayout } from 'layouts/NavAndFooter'
import { PageHead } from 'pages/Head'
import { injectI18n } from 'utils/i18n'
import { toPascalCase } from 'utils/toPascalCase'
import { PostPage } from 'components/Post/Page'
import type { Locales } from '@captaincause/types'
import type { NextPageWithLayout } from 'app'
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next'
import type { ComponentType } from 'react'
import type { StrapiTestimonialPost } from 'types/strapi/post'

interface PageProps {
  post: StrapiTestimonialPost
}

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const slugs = await getTestimonialPostsSlug()

    return {
      paths: slugs,
      fallback: 'blocking',
    }
  } catch (error: unknown) {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }
}

export const getStaticProps: GetStaticProps<PageProps> = async context => {
  if (context.locale !== 'fr') {
    return {
      notFound: true,
    }
  }

  const i18n = await injectI18n('fr', [
    'footer.component',
    'nav.component',
    'metadata',
  ])

  const banner = await getBanner({ locale: context.locale as Locales })

  try {
    const path = context.params?.slug ?? ''

    if (!path) {
      return { notFound: true }
    }

    const slug = Array.isArray(path) ? `/${path.join('/')}` : `/${path}`

    const post = await getTestimonialPostBySlug({
      id: slug,
      locale: context.locale as Locales,
    })

    return {
      props: { post, banner, ...i18n },
      revalidate: 120,
    }
  } catch (error: unknown) {
    return {
      notFound: true,
      revalidate: 120,
    }
  }
}

// ---

type InferredPageProps = InferGetStaticPropsType<typeof getStaticProps>
const TestimonialPost: NextPageWithLayout<InferredPageProps> = ({
  post,
}: PageProps) => {
  const memoizedBlocks = useMemo(
    () =>
      post.blocks.map(block => {
        const componentName = block.__component.split('.')[1]
        const pascalCasedComponentName = toPascalCase(componentName)

        return {
          Block: blocks[pascalCasedComponentName],
          ...block,
        }
      }),
    [post.blocks],
  )

  return (
    <>
      <PageHead
        head={{
          ...post.head,
          image: post.image,
        }}
      />

      <main
        id="main"
        className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-8 md:py-10"
      >
        <PostPage
          post={post}
          backLink={{ href: '/testimonials', title: 'Retour aux témoignages' }}
        >
          {memoizedBlocks.map(
            (
              { Block, ...block }: { Block: ComponentType; __component: any },
              index,
            ) => (
              <Block
                // We forge a complex key to prevent block transfer between page during pushState navigation
                // page.id : To have a sort of uniqness but the strapi ID is not complex (just a simple number)
                // page.slug : Should be uniq by page. But not enough because pages can be renamed
                // block.__component : Strapi name of the component. Two same blocks have the same name, hence the complex key
                // index : Is important, give the position of the block in this specific page
                // eslint-disable-next-line react/no-array-index-key
                key={`${post.id}-${post.slug}-${block.__component}-${index}`}
                {...block}
              />
            ),
          )}
        </PostPage>
      </main>
    </>
  )
}

TestimonialPost.Layout = NavAndFooterLayout
TestimonialPost.pagePropsOnLayout = true

export default TestimonialPost
